import React from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { VertebralNumberingBasis, VertebralNumberingDeclaration } from '../services/reportService';

interface Props {
  value: VertebralNumberingDeclaration;
  onChange: (value: VertebralNumberingDeclaration) => void;
  allowedBases?: VertebralNumberingBasis[];
  basisRequired?: boolean;
  disabled?: boolean;
}

const BASIS_LABELS: Record<VertebralNumberingBasis, string> = {
  c2_downward: 'Count downward from C2',
  t12_lowest_rib: 'T12 is lowest rib-bearing vertebra',
  l5_upward: 'Lowest mobile lumbar vertebra is L5',
  prior_correlation: 'Correlate with prior study',
};

const DEFAULT_BASES = Object.keys(BASIS_LABELS) as VertebralNumberingBasis[];

const VertebralNumberingDeclarationForm: React.FC<Props> = ({
  value,
  onChange,
  allowedBases = DEFAULT_BASES,
  basisRequired = true,
  disabled = false,
}) => {
  const mappings = Object.entries(value.level_mapping || {});
  const update = (patch: Partial<VertebralNumberingDeclaration>) => onChange({ ...value, ...patch });

  const updateMapping = (index: number, side: 'reported' | 'anatomical', nextValue: string) => {
    const rows = mappings.map(([reported, anatomical]) => ({ reported, anatomical }));
    rows[index][side] = nextValue.toUpperCase();
    update({
      level_mapping: Object.fromEntries(rows.filter((row) => row.reported).map((row) => [row.reported, row.anatomical])),
    });
  };

  const addMapping = () => {
    let number = 6;
    while (value.level_mapping[`L${number}`]) number += 1;
    update({ level_mapping: { ...value.level_mapping, [`L${number}`]: 'S1' } });
  };
  const removeMapping = (index: number) => update({
    level_mapping: Object.fromEntries(mappings.filter((_, rowIndex) => rowIndex !== index)),
  });

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" gutterBottom>Vertebral Numbering</Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
        Declare the numbering method used for this MRI spine report. RadNeo does not infer anatomy from images.
      </Typography>
      {!value.basis && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          {basisRequired ? 'A numbering basis is required for this spine region before final review.' : 'No basis selected; generation can continue with a review warning.'}
        </Alert>
      )}
      <Stack spacing={1.5}>
        <FormControl fullWidth size="small" disabled={disabled}>
          <InputLabel id="vertebral-basis-label">Numbering basis</InputLabel>
          <Select
            labelId="vertebral-basis-label"
            label="Numbering basis"
            value={value.basis || ''}
            onChange={(event) => update({ basis: (event.target.value || undefined) as VertebralNumberingBasis | undefined })}
          >
            <MenuItem value=""><em>Not declared</em></MenuItem>
            {allowedBases.map((basis) => <MenuItem key={basis} value={basis}>{BASIS_LABELS[basis]}</MenuItem>)}
          </Select>
        </FormControl>
        {(value.basis === 'prior_correlation' || value.basis) && (
          <TextField
            size="small"
            label={value.basis === 'prior_correlation' ? 'Prior study and date' : 'Reference details (optional)'}
            required={value.basis === 'prior_correlation'}
            value={value.reference_details || ''}
            onChange={(event) => update({ reference_details: event.target.value })}
            disabled={disabled}
            fullWidth
          />
        )}
        <FormControl fullWidth size="small" disabled={disabled}>
          <InputLabel id="vertebral-variant-label">Transitional anatomy</InputLabel>
          <Select
            labelId="vertebral-variant-label"
            label="Transitional anatomy"
            value={value.variant || ''}
            onChange={(event) => update({ variant: event.target.value || undefined, level_mapping: event.target.value ? value.level_mapping : {} })}
          >
            <MenuItem value="">None declared</MenuItem>
            <MenuItem value="lumbarized_s1">Lumbarized S1</MenuItem>
            <MenuItem value="sacralized_l5">Sacralized L5</MenuItem>
            <MenuItem value="rib_count_variant">Rib-count variant</MenuItem>
            <MenuItem value="other_declared">Other declared variant</MenuItem>
          </Select>
        </FormControl>
        {value.variant && (
          <>
            <TextField
              size="small"
              label="Radiologist variant wording"
              value={value.variant_description || ''}
              onChange={(event) => update({ variant_description: event.target.value })}
              disabled={disabled}
              fullWidth
            />
            <Box>
              <Typography variant="caption" fontWeight={600}>Explicit vertebral mapping</Typography>
              {mappings.map(([reported, anatomical], index) => (
                <Stack key={`${reported}-${index}`} direction="row" spacing={1} sx={{ mt: 1 }}>
                  <TextField size="small" label="Reported" value={reported} onChange={(event) => updateMapping(index, 'reported', event.target.value)} disabled={disabled} />
                  <TextField size="small" label="Anatomical" value={anatomical} onChange={(event) => updateMapping(index, 'anatomical', event.target.value)} disabled={disabled} />
                  <Button size="small" color="error" onClick={() => removeMapping(index)} disabled={disabled}>Remove</Button>
                </Stack>
              ))}
              <Button size="small" sx={{ mt: 1 }} onClick={addMapping} disabled={disabled}>Add mapping</Button>
            </Box>
          </>
        )}
      </Stack>
    </Paper>
  );
};

export default VertebralNumberingDeclarationForm;
